import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Flex,
  Heading,
  Text,
  Badge,
  SimpleGrid,
  Input,
  Select,
  HStack,
  VStack,
  Spinner,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  ModalFooter,
  Alert,
  AlertIcon,
  Tooltip,
} from '@chakra-ui/react';
import { FiPlus, FiEye, FiWifi, FiWifiOff, FiRefreshCw } from 'react-icons/fi';
import { CordeCommonModelsDtoMachineMachine } from '@/api/generated/v1/api';
import { useMachineService } from '../../hooks/useMachineService';
import { useMachineStatusStream } from '../../hooks/useMachineStatusStream';
import { useNavigate } from 'react-router-dom';

interface CreateMachineFormData {
  name: string;
  category: string;
  stream_url: string;
  stream_auth_token: string;
}

export const MachinesPage: React.FC = () => {
  console.log('[MachinesPage] Component rendering');
  
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const navigate = useNavigate();
  
  const [createForm, setCreateForm] = useState<CreateMachineFormData>({
    name: '',
    category: '',
    stream_url: '',
    stream_auth_token: '',
  });

  const machineService = useMachineService();
  const toast = useToast();

  console.log('[MachinesPage] Calling useMachineStatusStream hook');
  
  // Use WebSocket for real-time machine status updates
  const { machines, status: wsStatus, reconnect } = useMachineStatusStream({
    statusFilter: statusFilter || undefined,
    categoryFilter: categoryFilter || undefined,
    autoReconnect: true,
    maxReconnectAttempts: 5,
    reconnectDelay: 3000
  });
  
  console.log('[MachinesPage] WebSocket status:', wsStatus);

  // Debug: log machines state changes
  React.useEffect(() => {
    console.log('MachinesPage: machines state changed:', machines);
  }, [machines]);

  const handleCreateMachine = async () => {
    try {
      await machineService.createMachine(createForm);
      toast({
        title: 'Success',
        description: 'Machine created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onCreateClose();
      setCreateForm({ name: '', category: '', stream_url: '', stream_auth_token: '' });
      // No need to reload - WebSocket will receive the update automatically
    } catch (error) {
      console.error('Failed to create machine:', error);
      toast({
        title: 'Error',
        description: 'Failed to create machine',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleViewMachine = (machine: CordeCommonModelsDtoMachineMachine) => {
    navigate(`/machine/${machine.id}`);
  };

  const getStatusColor = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'free':
      case 'available':
        return 'green';
      case 'busy':
      case 'occupied':
        return 'blue';
      case 'disabled':
      case 'maintenance':
        return 'red';
      case 'offline':
        return 'gray';
      default:
        return 'gray';
    }
  };

  // Filter machines on client side (since WebSocket filters are also applied)
  const filteredMachines = useMemo(() => {
    return machines.filter(machine => {
      const matchesSearch = !searchQuery || 
        machine.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        machine.id?.toLowerCase().includes(searchQuery.toLowerCase());
      
      return matchesSearch;
    });
  }, [machines, searchQuery]);

  const categories = useMemo(() => {
    return Array.from(new Set(machines.map(m => m.category).filter(Boolean)));
  }, [machines]);

  // Show loading when not connected and no machines yet
  if (!wsStatus.connected && machines.length === 0) {
    return (
      <Container maxW="container.xl" py={8}>
        <Flex justify="center" align="center" h="200px" direction="column" gap={4}>
          <Spinner size="xl" />
          <VStack spacing={2}>
            <Text>Connecting to machine status stream...</Text>
            {wsStatus.error && (
              <Text color="red.500" fontSize="sm">
                {wsStatus.error}
              </Text>
            )}
          </VStack>
        </Flex>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="center">
          <HStack spacing={3}>
            <Heading size="lg">Gaming Machines</Heading>
            <Tooltip label={wsStatus.connected ? 'Connected to live updates' : 'Disconnected from live updates'}>
              <Box color={wsStatus.connected ? 'green.500' : 'red.500'}>
                {wsStatus.connected ? <FiWifi /> : <FiWifiOff />}
              </Box>
            </Tooltip>
            {machines.length > 0 && (
              <Badge colorScheme="blue" variant="subtle">
                {machines.length} machine{machines.length !== 1 ? 's' : ''}
              </Badge>
            )}
          </HStack>
          <HStack spacing={2}>
            {!wsStatus.connected && (
              <Button
                leftIcon={<FiRefreshCw />}
                size="sm"
                variant="outline"
                onClick={reconnect}
                isLoading={wsStatus.reconnectAttempts > 0}
              >
                Reconnect
              </Button>
            )}
            <Button
              leftIcon={<FiPlus />}
              colorScheme="blue"
              onClick={onCreateOpen}
            >
              Add Machine
            </Button>
          </HStack>
        </Flex>

        {/* Filters */}
        <Card>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <HStack spacing={4} wrap="wrap">
                <Input
                  placeholder="Search machines..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  maxW="300px"
                />
                
                <Select
                  placeholder="All statuses"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  maxW="200px"
                >
                  <option value="FREE">Free</option>
                  <option value="BUSY">Busy</option>
                  <option value="DISABLED">Disabled</option>
                  <option value="OFFLINE">Offline</option>
                </Select>
                
                <Select
                  placeholder="All categories"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  maxW="200px"
                >
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </Select>
              </HStack>
              
              {wsStatus.error && (
                <Alert status="warning" size="sm">
                  <AlertIcon />
                  <Text fontSize="sm">
                    Connection issue: {wsStatus.error}
                  </Text>
                  <Button size="xs" ml={2} onClick={reconnect} variant="outline">
                    Retry
                  </Button>
                </Alert>
              )}
            </VStack>
          </CardBody>
        </Card>

        {/* Machine Grid */}
        {filteredMachines.length === 0 ? (
          <Alert status="info">
            <AlertIcon />
            {machines.length === 0 
              ? 'No machines available. Create a new machine to get started.' 
              : 'No machines match your search criteria. Try adjusting your filters.'}
          </Alert>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            {filteredMachines.map((machine) => (
              <Card 
                key={machine.id} 
                h="fit-content"
                cursor="pointer"
                _hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
                transition="all 0.2s"
                onClick={() => handleViewMachine(machine)}
              >
                <CardHeader pb={2}>
                  <Flex justify="space-between" align="flex-start">
                    <VStack align="start" spacing={1} flex={1}>
                      <Heading size="md" noOfLines={1}>
                        {machine.name || 'Unnamed Machine'}
                      </Heading>
                      <Text fontSize="sm" color="gray.500" noOfLines={1}>
                        ID: {machine.id}
                      </Text>
                    </VStack>
                    <VStack align="end" spacing={1}>
                      <Badge
                        colorScheme={getStatusColor(machine.status)}
                        variant="solid"
                        size="sm"
                      >
                        {machine.status?.toUpperCase() || 'UNKNOWN'}
                      </Badge>
                      {machine.last_seen && (
                        <Text fontSize="xs" color="gray.400">
                          {new Date(machine.last_seen).toLocaleTimeString()}
                        </Text>
                      )}
                    </VStack>
                  </Flex>
                </CardHeader>
                
                <CardBody pt={0}>
                  <VStack align="start" spacing={3}>
                    {machine.category && (
                      <Text fontSize="sm">
                        <strong>Category:</strong> {machine.category}
                      </Text>
                    )}
                    
                    {machine.last_seen && (
                      <Text fontSize="sm">
                        <strong>Last Seen:</strong>{' '}
                        {new Date(machine.last_seen).toLocaleString()}
                      </Text>
                    )}
                    
                    {machine.stream_url && (
                      <Text fontSize="sm" color="green.500">
                        <strong>Stream:</strong> Available
                      </Text>
                    )}
                    
                    <Flex w="full" justify="space-between" align="center" mt={4}>
                      <Text fontSize="xs" color="gray.400">
                        Click to view details
                      </Text>
                      <FiEye color="gray" />
                    </Flex>
                  </VStack>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        )}
      </VStack>

      {/* Create Machine Modal */}
      <Modal isOpen={isCreateOpen} onClose={onCreateClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create New Machine</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Machine Name</FormLabel>
                <Input
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  placeholder="Enter machine name"
                />
              </FormControl>
              
              <FormControl>
                <FormLabel>Category</FormLabel>
                <Input
                  value={createForm.category}
                  onChange={(e) => setCreateForm({ ...createForm, category: e.target.value })}
                  placeholder="Enter category (e.g., slots, poker)"
                />
              </FormControl>
              
              <FormControl>
                <FormLabel>Stream URL</FormLabel>
                <Input
                  value={createForm.stream_url}
                  onChange={(e) => setCreateForm({ ...createForm, stream_url: e.target.value })}
                  placeholder="Enter video stream URL"
                />
              </FormControl>
              
              <FormControl>
                <FormLabel>Stream Auth Token</FormLabel>
                <Input
                  type="password"
                  value={createForm.stream_auth_token}
                  onChange={(e) => setCreateForm({ ...createForm, stream_auth_token: e.target.value })}
                  placeholder="Enter stream authentication token"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCreateClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleCreateMachine}
              isDisabled={!createForm.name}
            >
              Create Machine
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>


    </Container>
  );
};
